import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Receipt,
  Settings,
  Bell,
  Search,
  TrendingUp,
  Dog,
  Hotel,
  Footprints,
  ArrowUpRight,
  PawPrint,
} from "lucide-react";

const WEEK = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];
const BOOKINGS = {
  2: [{ name: "Bosse", color: "bg-emerald-500" }, { name: "Luna", color: "bg-slate-700" }],
  3: [{ name: "Charlie", color: "bg-emerald-500" }],
  4: [
    { name: "Rocky", color: "bg-amber-500" },
    { name: "Maja", color: "bg-emerald-500" },
    { name: "Zelda", color: "bg-slate-700" },
  ],
  5: [{ name: "Buster", color: "bg-emerald-500" }, { name: "Nala", color: "bg-rose-500" }],
  6: [{ name: "Doris", color: "bg-slate-700" }],
  9: [{ name: "Pixie", color: "bg-emerald-500" }],
  10: [{ name: "Sam", color: "bg-amber-500" }],
  11: [{ name: "Rufus", color: "bg-emerald-500" }, { name: "Vega", color: "bg-slate-700" }],
  12: [{ name: "Lola", color: "bg-emerald-500" }],
  13: [{ name: "Theo", color: "bg-rose-500" }],
  17: [{ name: "Bella", color: "bg-emerald-500" }, { name: "Otto", color: "bg-slate-700" }],
  18: [{ name: "Milo", color: "bg-amber-500" }],
  19: [{ name: "Frida", color: "bg-emerald-500" }, { name: "Zorro", color: "bg-emerald-500" }],
  20: [{ name: "Saga", color: "bg-rose-500" }],
};

export default function DashboardMockup() {
  return (
    <section data-testid="dashboard-mockup" className="relative pb-24 -mt-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="relative rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
          {/* Top browser-like bar */}
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 px-4 py-3">
            <div className="flex gap-1.5">
              <span className="h-3 w-3 rounded-full bg-rose-400" />
              <span className="h-3 w-3 rounded-full bg-amber-400" />
              <span className="h-3 w-3 rounded-full bg-emerald-400" />
            </div>
            <div className="mx-auto flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-mono">
              <span className="text-emerald-500">●</span> app.hundify.se / dashboard
            </div>
          </div>

          <div className="grid grid-cols-12">
            {/* Sidebar */}
            <aside className="hidden md:flex md:col-span-3 lg:col-span-2 flex-col gap-1 border-r border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 p-4">
              <div className="flex items-center gap-2 px-2 pb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 dark:bg-emerald-500 text-white">
                  <PawPrint className="h-4 w-4" />
                </div>
                <span className="font-display font-bold text-slate-900 dark:text-white">Hundify</span>
              </div>
              {[
                { i: LayoutDashboard, l: "Översikt", active: true },
                { i: CalendarDays, l: "Bokningar" },
                { i: Dog, l: "Hundar" },
                { i: Users, l: "Kunder" },
                { i: Receipt, l: "Fakturor" },
                { i: Settings, l: "Inställningar" },
              ].map(({ i: Icon, l, active }) => (
                <div
                  key={l}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${
                    active
                      ? "bg-emerald-500 text-white font-semibold shadow-sm"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {l}
                </div>
              ))}
            </aside>

            {/* Main */}
            <div className="col-span-12 md:col-span-9 lg:col-span-10 p-5 lg:p-8">
              {/* Top bar */}
              <div className="flex items-center justify-between gap-3 mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">
                    God morgon, Anna
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                    Översikt
                  </h3>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 w-56">
                    <Search className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-400">Sök hund, kund…</span>
                  </div>
                  <button className="relative h-10 w-10 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center">
                    <Bell className="h-4 w-4 text-slate-500 dark:text-slate-300" />
                    <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900" />
                  </button>
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center font-display font-bold">
                    A
                  </div>
                </div>
              </div>

              {/* Stat cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                  icon={Dog}
                  label="Aktiva hundar idag"
                  value="42"
                  delta="+8 ikväll"
                  positive
                  testid="stat-aktiva-hundar"
                />
                <StatCard
                  icon={Hotel}
                  label="Beläggning hotellet"
                  value="85%"
                  delta="+12% denna vecka"
                  positive
                  progress={85}
                  testid="stat-belaggning"
                />
                <StatCard
                  icon={Footprints}
                  label="Promenader idag"
                  value="17"
                  delta="3 pågående"
                  testid="stat-promenader"
                />
              </div>

              {/* Calendar + side */}
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Calendar */}
                <div
                  data-testid="dashboard-calendar"
                  className="lg:col-span-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">
                        Bokningskalender
                      </div>
                      <h4 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
                        Mars 2026
                      </h4>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                      <Legend color="bg-emerald-500" label="Hotell" />
                      <Legend color="bg-amber-500" label="Dagis" />
                      <Legend color="bg-rose-500" label="Promenad" />
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    {WEEK.map((d) => (
                      <div key={d}>{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: 35 }).map((_, idx) => {
                      const day = idx - 1; // start padding
                      const inMonth = day >= 1 && day <= 31;
                      const today = day === 12;
                      const events = BOOKINGS[day] || [];
                      return (
                        <div
                          key={idx}
                          className={`relative aspect-square rounded-lg p-1.5 border text-left text-xs transition-colors ${
                            today
                              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10"
                              : "border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                          } ${inMonth ? "text-slate-700 dark:text-slate-200" : "text-slate-300 dark:text-slate-700"}`}
                        >
                          <div className="font-display font-semibold">{inMonth ? day : ""}</div>
                          <div className="absolute bottom-1 left-1 right-1 flex gap-0.5 flex-wrap">
                            {events.slice(0, 3).map((e, i) => (
                              <span key={i} className={`h-1.5 w-1.5 rounded-full ${e.color}`} />
                            ))}
                            {events.length > 3 && (
                              <span className="text-[9px] font-bold text-slate-500">+{events.length - 3}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Today's schedule */}
                <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 dark:text-slate-400">
                        Idag
                      </div>
                      <h4 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
                        Schema
                      </h4>
                    </div>
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                      Visa alla <ArrowUpRight className="h-3 w-3" />
                    </span>
                  </div>
                  <ul className="space-y-3">
                    {[
                      { time: "08:00", name: "Bosse · Incheckning", tag: "Hotell", color: "bg-emerald-500" },
                      { time: "09:30", name: "Luna · Promenad 5 km", tag: "Walk", color: "bg-rose-500" },
                      { time: "11:00", name: "Charlie · Bad & klipp", tag: "Spa", color: "bg-amber-500" },
                      { time: "14:00", name: "Maja · Hämtas av Erik", tag: "Dagis", color: "bg-amber-500" },
                      { time: "16:30", name: "Zelda · Medicin (Apoquel)", tag: "Hotell", color: "bg-emerald-500" },
                    ].map((row) => (
                      <li key={row.time} className="flex items-center gap-3">
                        <span className={`h-9 w-9 rounded-lg ${row.color}/15 flex items-center justify-center font-display text-xs font-bold ${row.color.replace("bg-", "text-")}`} style={{ backgroundColor: "transparent" }}>
                          <span className={`absolute h-9 w-9 rounded-lg ${row.color} opacity-15`} />
                          <span className="relative">{row.time}</span>
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{row.name}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{row.tag}</div>
                        </div>
                        <span className={`h-2 w-2 rounded-full ${row.color}`} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          En riktig vy från Hundify Dashboard. Översikt över din verksamhet — i realtid.
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value, delta, positive, progress, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 transition-all hover:border-emerald-500/40"
    >
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
          <Icon className="h-5 w-5" />
        </div>
        <span
          className={`inline-flex items-center gap-1 text-xs font-semibold ${
            positive ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"
          }`}
        >
          {positive && <TrendingUp className="h-3 w-3" />}
          {delta}
        </span>
      </div>
      <div className="mt-4">
        <div className="text-sm text-slate-500 dark:text-slate-400">{label}</div>
        <div className="font-display text-3xl font-bold text-slate-900 dark:text-white mt-1">{value}</div>
      </div>
      {typeof progress === "number" && (
        <div className="mt-3 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`h-2 w-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}
